import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from './useSuiteStore'
import { useAuthStore } from './useAuthStore'

export interface AdminTicket {
  uuid: string
  externalReference: string
  internalReference: number
  title: string
  description: string
  ticketStatus: string
  tags: string[]
  assignedUserUuid: string | null
  assignedUser?: { uuid: string; firstName: string | null; lastName: string | null; email: string } | null
  ticketRequester?: {
    user?: { uuid: string; firstName: string | null; lastName: string | null; email: string } | null
    contact?: { uuid: string; firstname: string | null; lastname: string | null; email: string } | null
  }
  source?: { uuid: string; name: string; modelConnector: string }
  dateCreated: string
  dateUpdated: string
  lastSyncedAt: string | null
  lastExternalUpdatedAt: string | null
  // Détail enrichi (find one)
  comments?: AdminTicketComment[]
  attachments?: unknown[]
  rawTicket?: Record<string, unknown>
}

interface AdminTicketComment {
  comment: string
  author: string
  authorDetail?: string
  supportComment: boolean
  publicComment: boolean
  date: number
  attachments?: unknown[]
}

export interface AdminTicketFilters {
  tag?: string
  ticketStatus?: string[]
  assignedUserUuid?: string
  search?: string
  page?: number
  pageSize?: number
}

export const useTicketAdminStore = defineStore('ticketAdmin', () => {
  const suiteStore = useSuiteStore()
  const authStore = useAuthStore()

  const tickets = ref<AdminTicket[]>([])
  const total = ref(0)
  const page = ref(1)
  const pageSize = ref(50)
  const ticket = ref<AdminTicket | null>(null)
  const loading = ref(false)

  function buildQuery(filters: AdminTicketFilters): string {
    const params = new URLSearchParams()
    if (filters.tag) params.append('tag', filters.tag)
    if (filters.ticketStatus && filters.ticketStatus.length > 0) {
      filters.ticketStatus.forEach((s) => params.append('ticketStatus', s))
    }
    if (filters.assignedUserUuid) params.append('assignedUserUuid', filters.assignedUserUuid)
    if (filters.search) params.append('search', filters.search)
    if (filters.page) params.append('page', String(filters.page))
    if (filters.pageSize) params.append('pageSize', String(filters.pageSize))
    return params.toString()
  }

  async function fetchStats(query: { from?: string; to?: string; boardId?: number } = {}) {
    loading.value = true
    try {
      const params = new URLSearchParams()
      if (query.from) params.append('from', query.from)
      if (query.to) params.append('to', query.to)
      if (query.boardId != null) params.append('boardId', String(query.boardId))
      const qs = params.toString()
      const res = await suiteStore.suiteFetch(`/admin/tickets/stats${qs ? `?${qs}` : ''}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })
      if (!res || !res.ok) throw new Error('Echec de la récupération des stats')
      return await res.json()
    } finally {
      loading.value = false
    }
  }

  async function exportStats(query: { from?: string; to?: string; boardId?: number; format?: 'csv' | 'xlsx' } = {}) {
    const params = new URLSearchParams()
    if (query.from) params.append('from', query.from)
    if (query.to) params.append('to', query.to)
    if (query.boardId != null) params.append('boardId', String(query.boardId))
    if (query.format) params.append('format', query.format)
    const qs = params.toString()
    const res = await suiteStore.suiteFetch(`/admin/tickets/stats/export${qs ? `?${qs}` : ''}`, {
      method: 'GET',
      credentials: 'include',
    })
    if (!res || !res.ok) throw new Error('Echec de l\'export')
    const blob = await res.blob()
    const filename =
      res.headers.get('Content-Disposition')?.match(/filename="?([^";]+)"?/)?.[1] ??
      `tickets-stats.${query.format ?? 'xlsx'}`
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  async function fetchTickets(filters: AdminTicketFilters = {}) {
    loading.value = true
    try {
      const qs = buildQuery({ tag: 'DEV', page: 1, pageSize: 50, ...filters })
      const res = await suiteStore.suiteFetch(`/admin/tickets${qs ? `?${qs}` : ''}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })
      if (!res || !res.ok) throw new Error('Echec de la récupération des tickets admin')
      const data = await res.json()
      tickets.value = data.items ?? []
      total.value = data.total ?? 0
      page.value = data.page ?? 1
      pageSize.value = data.pageSize ?? 50
      return data
    } finally {
      loading.value = false
    }
  }

  async function fetchTicket(uuid: string) {
    loading.value = true
    try {
      const res = await suiteStore.suiteFetch(`/admin/tickets/${uuid}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })
      if (!res || !res.ok) throw new Error('Echec de la récupération du ticket admin')
      ticket.value = await res.json()
      return ticket.value
    } finally {
      loading.value = false
    }
  }

  async function addComment(uuid: string, comment: string, publicComment: boolean, files?: File[]) {
    loading.value = true
    try {
      const formData = new FormData()
      formData.append('comment', comment)
      formData.append('publicComment', String(publicComment))
      if (files && files.length > 0) files.forEach((f) => formData.append('files', f))

      const res = await suiteStore.suiteFetch(`/admin/tickets/${uuid}/comment`, {
        method: 'POST',
        headers: { 'x-csrf-token': authStore.getCoreCsrfToken() },
        credentials: 'include',
        body: formData,
      })
      if (!res || !res.ok) throw new Error("Echec de l'ajout du commentaire")
      // Refresh ticket pour récupérer le commentaire posté
      await fetchTicket(uuid)
    } finally {
      loading.value = false
    }
  }

  async function updateTags(uuid: string, tags: string[]) {
    loading.value = true
    try {
      const res = await suiteStore.suiteFetch(`/admin/tickets/${uuid}/tags`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-csrf-token': authStore.getCoreCsrfToken(),
        },
        credentials: 'include',
        body: JSON.stringify({ tags }),
      })
      if (!res || !res.ok) throw new Error('Echec de la mise à jour des tags')
      await fetchTicket(uuid)
    } finally {
      loading.value = false
    }
  }

  async function updateStatus(uuid: string, status: string) {
    loading.value = true
    try {
      const res = await suiteStore.suiteFetch(`/admin/tickets/${uuid}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-csrf-token': authStore.getCoreCsrfToken(),
        },
        credentials: 'include',
        body: JSON.stringify({ ticketStatus: status }),
      })
      if (!res || !res.ok) throw new Error('Echec du changement de statut')
      await fetchTicket(uuid)
    } finally {
      loading.value = false
    }
  }

  async function fetchRelatedRequest(uuid: string) {
    const res = await suiteStore.suiteFetch(`/admin/tickets/${uuid}/related-request`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    })
    if (!res || !res.ok) return null
    return (await res.json().catch(() => null)) as null | { _id: string; code: string; status: string }
  }

  async function transformToRequest(uuid: string, options: { commentBack?: boolean; closeTicket?: boolean } = {}) {
    loading.value = true
    try {
      const res = await suiteStore.suiteFetch(`/admin/tickets/${uuid}/to-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-csrf-token': authStore.getCoreCsrfToken(),
        },
        credentials: 'include',
        body: JSON.stringify(options),
      })
      if (!res || !res.ok) throw new Error('Echec de la transformation')
      const data = await res.json()
      await fetchTicket(uuid)
      return data as { request: { _id: string; code: string }; alreadyExisted: boolean }
    } finally {
      loading.value = false
    }
  }

  /**
   * userUuid omis → s'attribue à soi-même côté backend.
   * userUuid === null → désassigner.
   */
  async function assign(uuid: string, userUuid?: string | null) {
    loading.value = true
    try {
      const body: Record<string, unknown> = {}
      if (userUuid !== undefined) body.userUuid = userUuid

      const res = await suiteStore.suiteFetch(`/admin/tickets/${uuid}/assign`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-csrf-token': authStore.getCoreCsrfToken(),
        },
        credentials: 'include',
        body: JSON.stringify(body),
      })
      if (!res || !res.ok) throw new Error("Echec de l'attribution")
      await fetchTicket(uuid)
    } finally {
      loading.value = false
    }
  }

  return {
    tickets,
    total,
    page,
    pageSize,
    ticket,
    loading,
    fetchTickets,
    fetchTicket,
    addComment,
    updateStatus,
    updateTags,
    assign,
    fetchStats,
    exportStats,
    transformToRequest,
    fetchRelatedRequest,
  }
})
